import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
} from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useValidateAndGetLandwirtDataMutation } from "store/masterdata";
import { ValidateAndGetLandwirtData } from "types/masterdata.types";

interface Props {
  initialValues: {
    kundennummer?: number;
    firstname?: string;
    lastname?: string;
    email?: string;
    phone?: string;
    postalCode?: string;
    setValidated?: boolean;
  };
  visible: boolean;
  onClose(): void;
  onSuccess(data: ValidateAndGetLandwirtData): void;
}

function ValidationForm({
  initialValues,
  visible,
  onClose,
  onSuccess,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [validate, validationResult] = useValidateAndGetLandwirtDataMutation();

  const [alert, setAlert] = useState<{
    visible: boolean;
    message: string;
    type: "success" | "info" | "warning" | "error";
  }>({
    visible: false,
    message: "",
    type: "success",
  });

  const handleSubmit = (values: {
    kundennummer: number;
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    postalCode: string;
    setValidated: boolean;
  }) => {
    const filledFieldsCount = Object.values(values).filter(
      (value) => value
    ).length;
    if (filledFieldsCount === 1 || filledFieldsCount === 2) {
      setAlert({
        visible: true,
        message: t("masterdata.lw_details.validation_fields_error"),
        type: "error",
      });
    } else {
      validate(values);
    }
  };

  useEffect(() => {
    if (validationResult.isSuccess) {
      if (validationResult.data.result.isValidated) {
        onSuccess(validationResult.data);
        setAlert({
          visible: true,
          message: t("masterdata.lw_details.validation_success"),
          type: "success",
        });
      } else {
        setAlert({
          visible: true,
          message: t("masterdata.lw_details.validation_error"),
          type: "error",
        });
      }
    }
  }, [validationResult]);

  return (
    <Modal
      footer={null}
      title={t("masterdata.lw_details.validation")}
      visible={visible}
      onCancel={onClose}
      destroyOnClose
    >
      <Form
        id="validation-form"
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        initialValues={initialValues}
      >
        <p>{t("masterdata.lw_details.validation_message")}</p>
        {alert.visible && <Alert message={alert.message} type={alert.type} />}
        <Divider />
        <Form.Item
          label={t("masterdata.lw_details.kundennummer")}
          name="kundennummer"
        >
          <Input disabled />
        </Form.Item>
        <Form.Item label={t("masterdata.lw_details.lastname")} name="lastname">
          <Input />
        </Form.Item>
        <Form.Item
          label={t("masterdata.lw_details.firstname")}
          name="firstname"
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={t("masterdata.lw_details.email")}
          name="email"
          rules={[{ type: "email" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item label={t("masterdata.lw_details.phone")} name="phone">
          <Input />
        </Form.Item>
        <Form.Item
          label={t("masterdata.lw_details.postal_code")}
          name="postalCode"
        >
          <Input />
        </Form.Item>
        <Form.Item name="setValidated" valuePropName="checked">
          <Checkbox>{t("masterdata.lw_details.save_validation")}</Checkbox>
        </Form.Item>
        <Row gutter={20} justify="center">
          {alert.visible && alert.type === "success" ? (
            <Col span={12}>
              <Button onClick={onClose} type="primary" block>
                {t("masterdata.lw_details.conclude")}
              </Button>
            </Col>
          ) : (
            <>
              <Col span={12}>
                <Button onClick={onClose} block>
                  {t("masterdata.lw_details.abort")}
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={validationResult.isLoading}
                  block
                >
                  {t("masterdata.lw_details.confirm")}
                </Button>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </Modal>
  );
}

export default ValidationForm;
